import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Check } from 'lucide-react';

import useTenantState from '@/states/tenant.state';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import useOrgChartState, { IOrgChart } from '@/states/organization.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import { sortRows } from '@/utils/functions/sortRows';
import ReportTable from '@/components/company/report/ReportTable';
import ReportFilters from '@/components/company/report/ReportFilters';
import { filterJobDescriptions } from '@/components/company/report/report.utils';

/** Seluruh uraian tugas satu jabatan diambil sekali lalu disaring dan dipaginasi di sisi klien. */
const ALL_ROWS_PAGE_SIZE = 1000;

interface RowDraft {
  report?: string;
  file?: File | null;
  isDeleted?: boolean;
}

/** Pesan penolakan file template, atau null bila file PDF berukuran maksimal 1 MB. */
function getFileRejection(file: File): string | null {
  if (file.type !== 'application/pdf') return `File ${file.name} harus berformat PDF`;
  if (file.size > 1 * 1024 * 1024) return `Ukuran file ${file.name} tidak boleh lebih dari 1 MB`;
  return null;
}

function findNodeById(nodes: IOrgChart[], id: number): IOrgChart | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = node.children?.length ? findNodeById(node.children, id) : null;
    if (found) return found;
  }
  return null;
}

export default function ReportDetail() {
  const navigate = useNavigate();
  const { tenant_id, nodeId } = useParams<{ tenant_id: string; nodeId: string }>();
  const nodeIdNum = Number(nodeId);
  const { currentTenant } = useTenantState();
  const isReadOnly = useIsReadOnly();
  const [isSaving, setIsSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const { getJobDescriptions, clearJobDescriptions, paginatedData, updateJobDescription, addReportFile, isLoading } =
    useJobDescriptionState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();

  const [pageConfig, setPageConfig] = useState<DataConfig>({ page: 0, limit: 10, orderBy: '', order: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');

  const resetToFirstPage = useCallback(() => setPageConfig(prev => ({ ...prev, page: 0 })), []);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const handleActivityChange = useCallback(
    (value: string) => {
      setSelectedActivity(value);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedActivity('');
    resetToFirstPage();
  }, [resetToFirstPage]);

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id || !nodeIdNum) return;

    try {
      await Promise.all([
        fetchOrgChartTree(currentTenant.id),
        getJobDescriptions(currentTenant.id, {
          page: 0,
          page_size: ALL_ROWS_PAGE_SIZE,
          org_chart_node_id: nodeIdNum,
        }),
      ]);
    } catch {
      toast.error('Gagal mengambil data');
    }
  }, [currentTenant?.id, nodeIdNum, fetchOrgChartTree, getJobDescriptions]);

  useEffect(() => {
    clearJobDescriptions();
    fetchData();
  }, [clearJobDescriptions, fetchData]);

  const allRows = useMemo(
    () => ((paginatedData?.content ?? []) as IJobDescription[]).filter(r => r.org_chart_node_id === nodeIdNum),
    [paginatedData, nodeIdNum],
  );

  const peranName = findNodeById(orgCharts, nodeIdNum)?.name ?? allRows[0]?.org_chart_node?.name ?? '';

  const filteredRows = useMemo(
    () => filterJobDescriptions(allRows, searchTerm, '', selectedActivity),
    [allRows, searchTerm, selectedActivity],
  );

  const sortedContent = useMemo(() => {
    const accessors: Record<string, (row: IJobDescription) => unknown> = {
      name: row => row.created_at ?? null,
      frequency: row => row.created_at ?? null,
      report: row => row.created_at ?? null,
      org_key_activity_id: row => row.org_key_activity?.name ?? null,
    };
    if (!pageConfig.orderBy || !accessors[pageConfig.orderBy]) return filteredRows;
    return sortRows(filteredRows, pageConfig, accessors);
  }, [filteredRows, pageConfig]);

  const displayData = useMemo(() => {
    const start = pageConfig.page * pageConfig.limit;
    return {
      content: sortedContent.slice(start, start + pageConfig.limit),
      totalElements: sortedContent.length,
      totalPages: Math.max(1, Math.ceil(sortedContent.length / pageConfig.limit)),
      size: pageConfig.limit,
      number: pageConfig.page,
    };
  }, [sortedContent, pageConfig.page, pageConfig.limit]);

  const handleDraftChange = (id: number, patch: Partial<RowDraft>) => {
    setDrafts(prev => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const backToList = () => navigate(`/${tenant_id}/report`);

  const handleSaveAndBack = async () => {
    if (!currentTenant?.id) return;

    const dirtyIds = Object.keys(drafts).map(Number);
    if (dirtyIds.length === 0) {
      backToList();
      return;
    }

    setIsSaving(true);
    let hasRejected = false;
    try {
      for (const id of dirtyIds) {
        const draft = drafts[id];
        // Dicari di seluruh baris, bukan halaman yang tampil, agar editan yang
        // tergeser keluar oleh pencarian/filter/pindah halaman tetap tersimpan.
        const original = allRows.find(r => Number(r.id) === id);
        if (!original) continue;

        let formattedReportFile = original.report_file ?? null;

        if (draft.isDeleted) {
          formattedReportFile = '';
        } else if (draft.file) {
          const rejection = getFileRejection(draft.file);
          if (rejection) {
            toast.error(rejection);
            hasRejected = true;
            continue;
          }
          const uploadRes = await addReportFile(currentTenant.id, id, draft.file);
          formattedReportFile = JSON.stringify([uploadRes]);
        }

        await updateJobDescription(currentTenant.id, id, {
          org_chart_node_id: original.org_chart_node_id,
          org_key_activity_id: original.org_key_activity_id,
          name: original.name,
          report: draft.report || original.report || '',
          frequency: original.frequency ?? '',
          kpi: original.kpi ?? '',
          kpi_measurement: original.kpi_measurement ?? '',
          is_published: original.is_published ?? false,
          report_file: formattedReportFile,
        });
        setDrafts(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => Number(key) !== id)));
      }

      if (hasRejected) return;

      toast.success('Laporan berhasil disimpan');
      await refetchCompletionStatus();
      backToList();
    } catch {
      toast.error('Gagal menyimpan laporan');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="w-full">
      <div className="p-5 flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={backToList}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-primary self-start px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={14} /> Kembali ke Daftar Jabatan
          </button>
          {peranName && <p className="font-bold text-blue-700">{peranName}</p>}
        </div>

        {!isReadOnly && (
          <button
            type="button"
            onClick={handleSaveAndBack}
            disabled={isSaving}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60 self-start"
            style={{ background: '#FD6118' }}
          >
            <Check size={15} />
            {isSaving ? 'Menyimpan...' : 'Simpan & Kembali'}
          </button>
        )}
      </div>

      <ReportFilters
        showRoleFilter={false}
        jobDescriptions={allRows}
        searchTerm={searchTerm}
        selectedRole=""
        selectedActivity={selectedActivity}
        onSearch={handleSearch}
        onRoleChange={() => undefined}
        onActivityChange={handleActivityChange}
        onClear={handleClearFilters}
      />

      <div className="p-5">
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <ReportTable
            isLoading={isLoading}
            paginatedData={displayData}
            pageConfig={pageConfig}
            setPageConfig={setPageConfig}
            drafts={drafts}
            onDraftChange={handleDraftChange}
            showActions={!isReadOnly}
          />
        </div>
      </div>
    </section>
  );
}
